import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';

/**
 * ProtectedRoute
 *   - Blocks unauthenticated users (redirects to /login)
 *   - Optionally enforces a minimum RBAC level on a given resource.
 *     When blocked, renders a "Kein Zugriff"-Panel instead of redirecting
 *     so the sidebar stays usable.
 *
 * Usage:
 *   <ProtectedRoute><Page /></ProtectedRoute>
 *   <ProtectedRoute resource="invoices" requiredLevel="read"><Page /></ProtectedRoute>
 *   <ProtectedRoute anyResource={['tariffs','discounts']}><Page /></ProtectedRoute>
 */
export default function ProtectedRoute({ children, resource, anyResource, requiredLevel = 'read' }) {
  const { user, loading } = useAuth();
  const { loaded: permsLoaded, can, isAppOnly } = usePermissions();
  const needsPerms = !!(resource || anyResource);

  if (loading || (needsPerms && !permsLoaded)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red mx-auto"></div>
          <p className="mt-4 text-sm text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password && !user.impersonating) {
    return <Navigate to="/passwort-aendern" replace />;
  }

  // Reine App-Konten gehören in die Mobile-App-Ansicht, nicht in die Verwaltung
  if (permsLoaded && isAppOnly) {
    localStorage.removeItem('user');
    window.location.href = '/m';
    return null;
  }

  const allowed = anyResource?.length
    ? anyResource.some((r) => can(r, requiredLevel))
    : (!resource || can(resource, requiredLevel));

  if (!allowed) {
    const label = anyResource?.length ? anyResource.join(' / ') : resource;
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-slate-50 p-8"
        data-testid="rbac-access-denied"
      >
        <div className="max-w-md w-full rounded-xl border border-rose-200 bg-white p-8 shadow-sm text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <span className="text-2xl">🔒</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Kein Zugriff</h1>
          <p className="text-sm text-slate-600 mb-4">
            Deine aktuelle Rolle hat keine Berechtigung für diesen Bereich
            („{label}" · mindestens {requiredLevel}).
          </p>
          <p className="text-xs text-slate-400">
            Bitte wende dich an die Administrator:innen, wenn du hier Zugriff benötigst.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
